import {
  Box,
  Chip,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DownloadSimple, DownloadSimpleIcon, PencilSimple, PencilSimpleIcon } from "@phosphor-icons/react";
import { Project } from "../../../models/Project";
import { StyledProjectData } from "./projectData.styled";
import { useContext, useState } from "react";
import { LanguageContext } from "../../../globals/LanguageContext";
import {
  formatDateToDDMMYYYY,
  formatDateToYYYYMMDD,
} from "../../../globals/utils";
import { useProject } from "../../../stores/useProject";
import { useProfile } from "../../../stores/useProfile";

type ProjectDataProps = {
  project: Project;
};

const defaultData = {
  description: "",
  startDate: new Date(),
  endDate: new Date(),
  plannedWorkHours: 0,
  workedHours: 0,
  status: "",
  tags: [],
};

export const ProjectData = ({ project }: ProjectDataProps) => {
  const defaultValues = project ? project : defaultData;

  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({ ...defaultValues });
  const { dictionary } = useContext(LanguageContext);
  const { editProject } = useProject();
  const { user } = useProfile();

  const handleChanges = async () => {
    if (!user || !project.id) return;
    const updatedProject: Project = {
      ...project,
      ...formData,
    };

    await editProject(user, updatedProject, project.id);

    setEditMode(false);
  };

  const handleInputChange = <K extends keyof Project>(
    field: K,
    value: Project[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <StyledProjectData editMode={editMode}>
      <Box className="head">
        <Typography variant="h3">
          {dictionary.pages.projects.project.data.title}
        </Typography>
        {editMode ? (
          <DownloadSimpleIcon
            size={32}
            className="button"
            onClick={handleChanges}
            weight="bold"
            style={{ cursor: "pointer" }}
          />
        ) : (
          <PencilSimpleIcon
            size={32}
            weight="bold"
            className="button"
            onClick={() => setEditMode(true)}
            style={{ cursor: "pointer" }}
          />
        )}
      </Box>
      <Box className="content">
        <Box className="row">
          {editMode ? (
            <TextField
              fullWidth
              multiline
              InputProps={{
                rows: 2,
              }}
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
            />
          ) : (
            <Typography variant="h4">{formData.description}</Typography>
          )}
        </Box>
        <Box className="row">
          <Typography variant="h4" className="contactInfo">
            {dictionary.pages.projects.project.data.startAndEndDate}:
          </Typography>
          {editMode ? (
            <>
              <TextField
                type="date"
                value={
                  formData.startDate
                    ? formatDateToYYYYMMDD(formData.startDate)
                    : ""
                }
                onChange={(e) =>
                  handleInputChange("startDate", new Date(e.target.value))
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                type="date"
                value={
                  formData.endDate ? formatDateToYYYYMMDD(formData.endDate) : ""
                }
                onChange={(e) =>
                  handleInputChange("endDate", new Date(e.target.value))
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </>
          ) : (
            <>
              <Typography variant="h4">
                {formData.startDate
                  ? formatDateToDDMMYYYY(formData.startDate)
                  : "00/00/0000"}
              </Typography>
              <Typography variant="h4">
                {formData.startDate
                  ? formatDateToDDMMYYYY(formData.endDate)
                  : "00/00/0000"}
              </Typography>
            </>
          )}
        </Box>
        <Box className="row hours">
          <Typography variant="h4" className="contactInfo">
            {dictionary.pages.projects.project.data.plannedAndWorkedHours}:
          </Typography>
          {editMode ? (
            <>
              <TextField
                value={formData.plannedWorkHours}
                onChange={(e) =>
                  handleInputChange("plannedWorkHours", Number(e.target.value))
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                value={formData.workedHours}
                onChange={(e) =>
                  handleInputChange("workedHours", Number(e.target.value))
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </>
          ) : (
            <>
              <Typography variant="h4">{formData.plannedWorkHours ?? 0}</Typography>
              <Typography variant="h4">/</Typography>
              <Typography variant="h4">{formData.workedHours ?? 0}</Typography>
            </>
          )}
        </Box>
        <Box className="row">
          <Typography variant="h4" className="contactInfo">
            {dictionary.pages.projects.project.data.status}:
          </Typography>
          {editMode ? (
            <Select
              displayEmpty
              notched
              defaultValue={project?.status || ""}
              onChange={(e) => handleInputChange("status", e.target.value)}
            >
              <MenuItem value="">
                <em>
                  {dictionary.projectform.choosestatus || "Choose status"}
                </em>
              </MenuItem>
              <MenuItem value="Created">Created</MenuItem>
              <MenuItem value="QuoteSent">Quote Sent</MenuItem>
              <MenuItem value="InProgress">In Progress</MenuItem>
              <MenuItem value="Finished">Finished</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
            </Select>
          ) : (
            <Typography variant="h4">
              {formData.status || dictionary.projectform.choosestatus}
            </Typography>
          )}
        </Box>
        <Box className="row">
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {(formData.tags || []).map((tag) => (
              <Chip key={tag.id} label={tag.name} />
            ))}
          </Box>
        </Box>
      </Box>
    </StyledProjectData>
  );
};
